// ABOUTME: GraphQL lookups of the bulk change dialog: the hierarchy list, a node search by code or name, nodes by id.
// ABOUTME: Kept behind this module boundary so the component tests mock one file instead of Apollo.

import { SearchFilterOperator } from '@src/__generated__/types';
import { useCompactLinkSearchQuery } from '@src/components/formFields/compactLink.generated';
import { useHierarchiesQuery } from '@src/components/hierarchies/Hierarchy.generated';
import { useEffect, useMemo, useState } from 'react';

export type NodeOption = { id: string; code: string; listName: string };

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 30;
/** Ids resolved per query: a rows page holds at most a few hundred link ids. */
const BY_ID_LIMIT = 500;

type NodesData = ReturnType<typeof useCompactLinkSearchQuery>['data'];

const toOptions = (data: NodesData): NodeOption[] =>
    (data?.searchHierarchies.nodes.edges ?? []).flatMap(edge =>
        edge?.node
            ? [
                  {
                      id: edge.node.id,
                      code: edge.node.code,
                      listName: edge.node.listName ?? '',
                  },
              ]
            : [],
    );

export function useHierarchyList(skip: boolean) {
    const { data, loading } = useHierarchiesQuery({ skip });
    const hierarchies = useMemo(
        () =>
            (data?.hierarchies ?? [])
                .flatMap(h => (h ? [{ id: h.id, code: h.code }] : []))
                .sort((a, b) => a.code.localeCompare(b.code)),
        [data],
    );
    return { hierarchies, loading };
}

/** Nodes of a hierarchy whose code or list name contains the typed text. */
export function useNodeSearch(hierarchyId: string | null, text: string) {
    const [debounced, setDebounced] = useState(text);
    useEffect(() => {
        const timeout = setTimeout(() => setDebounced(text.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [text]);

    const { data, loading } = useCompactLinkSearchQuery({
        skip: !hierarchyId,
        variables: {
            ids: hierarchyId ? [hierarchyId] : [],
            filter: debounced
                ? [
                      {
                          field: 'codeOrListName',
                          operator: SearchFilterOperator.Contains,
                          value: [debounced],
                      },
                  ]
                : [],
            sort: [],
            first: SEARCH_LIMIT,
        },
        fetchPolicy: 'cache-first',
    });
    return { options: useMemo(() => toOptions(data), [data]), loading };
}

/** The nodes of the given ids, in the given hierarchies: one query for every id. */
export function useNodesById(hierarchyIds: string[], ids: string[]) {
    const uniqueIds = useMemo(() => Array.from(new Set(ids)).sort(), [ids]);
    const uniqueHierarchies = useMemo(
        () => Array.from(new Set(hierarchyIds)).sort(),
        [hierarchyIds],
    );
    const { data, loading } = useCompactLinkSearchQuery({
        skip: !uniqueIds.length || !uniqueHierarchies.length,
        variables: {
            ids: uniqueHierarchies,
            filter: [
                {
                    field: 'id',
                    operator: SearchFilterOperator.IsAnyOf,
                    value: uniqueIds.slice(0, BY_ID_LIMIT),
                },
            ],
            sort: [],
            first: BY_ID_LIMIT,
        },
        fetchPolicy: 'cache-first',
    });
    const byId = useMemo(
        () => new Map(toOptions(data).map(node => [node.id, node] as const)),
        [data],
    );
    return { byId, loading };
}
